import { Component } from '@angular/core';
import { Router } from '@angular/router';
import Customer from '../../../models/Customer';
import { RepositoryService } from '../../services/repository/repository.service';
import { AngularFireAuth } from '@angular/fire/auth';
import { Api } from '../../../api/api';
import { HomePage } from '../home/home.page';
import { Gender } from '../../../enums/Gender';
import { TranslateService } from '@ngx-translate/core';
import { AlertController, ModalController, PickerController } from '@ionic/angular';
import { customerBody } from '../../../models/Converter';
import { SubwayCardModalComponent } from '../../components/subway-card-modal/subway-card-modal.component';
import validator from 'validator';
import { environment } from '../../../environments/environment';
import RepositoryInterface from '../repository-interface';
import { EmailConfirmationPage } from '../email-confirmation/email-confirmation.page';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MenuPage } from '../menu/menu.page';
import { ValidationUtils } from '../../../utils/validation-utils';

@Component({
	selector: 'app-account',
	templateUrl: './account.page.html',
	styleUrls: ['account.page.scss'],
})
export class AccountPage extends RepositoryInterface {
	static url = 'account';
	static countryList = ['DE', 'AU'];

	customer: Customer;
	loading = false;

	gender = Gender;

	constructor(
		public repository: RepositoryService,
		private auth: AngularFireAuth,
		private router: Router,
		private snackbarCtrl: MatSnackBar,
		private translate: TranslateService,
		private pickerCtrl: PickerController,
		private modalCtrl: ModalController,
		private alertCtrl: AlertController
	) {
		super(repository);
	}

	static async navigate(router: Router) {
		await router.navigateByUrl(AccountPage.url);
	}

	onUser() {
		super.onUser();
		if (this.user) {
			if (this.user.isAnonymous) {
				HomePage.navigate(this.router);
			} else if (!this.user.emailVerified) {
				EmailConfirmationPage.navigate(this.router, true);
			}
		}
	}

	async saveCustomer() {
		if (!this.user || !this.customer) {
			this.snackbarCtrl.open(this.translate.instant('account.please_wait'), null, {
				duration: 2000,
			});
			return;
		}
		if (!this.validate()) {
			return;
		}
		this.loading = true;
		try {
			let response;
			if (!this.customer._id) {
				response = await Api.createCustomer(customerBody(this.customer));
			} else {
				response = await Api.patchCustomer(
					environment.customerGroup + '_' + this.repository._user.uid,
					customerBody(this.customer)
				);
			}
			this.snackbarCtrl.open(this.translate.instant('account.success'), null, {
				duration: 2000,
			});
			this.customer = response.data;
			this.repository.customer.emit(this.customer);
		} catch (e) {
			this.snackbarCtrl.open(this.translate.instant('account.error'), null, {
				duration: 2000,
			});
		}
		this.loading = false;
	}

	async openCountrySelector() {
		const options = {
			columns: [
				{
					name: 'country',
					class: 'country-picker',
					options: AccountPage.countryList.map(country => {
						return {
							text: this.translate.instant('country.' + country.toLowerCase()),
							value: country,
						};
					}),
				},
			],
			buttons: [
				{
					text: this.translate.instant('account.country_selection.cancel'),
					role: 'cancel',
				},
				{
					text: this.translate.instant('account.country_selection.save'),
					handler: value => {
						this.customer.country = value.country.value;
					},
				},
			],
		};
		const picker = await this.pickerCtrl.create(options);

		await picker.present();
	}

	async openModal() {
		await SubwayCardModalComponent.openModal(this.modalCtrl);
	}

	async home() {
		await this.repository.reloadCustomer(this.repository._user);
		await HomePage.navigate(this.router);
	}

	validate(): boolean {
		if (!this.customer.name || this.customer.name.length < 3) {
			this.snackbarCtrl.open(this.translate.instant('errors.name'), null, {
				duration: 2000,
			});
			return false;
		}
		if (!this.customer.email || !validator.isEmail(this.customer.email)) {
			this.snackbarCtrl.open(this.translate.instant('errors.email'), null, {
				duration: 2000,
			});
			return false;
		}
		/*if (!ValidationUtils.validatePhone(this.customer.phone)) {
			this.snackbarCtrl.open(this.translate.instant('errors.phone'), null, {
				duration: 2000,
			});
			return false;
		}*/
		if (!this.customer.street || this.customer.street.length < 1) {
			this.snackbarCtrl.open(this.translate.instant('errors.street'), null, {
				duration: 2000,
			});
			return false;
		}
		if (!this.customer.number || this.customer.number.length < 1) {
			this.snackbarCtrl.open(this.translate.instant('errors.street_number'), null, {
				duration: 2000,
			});
			return false;
		}
		if (!this.customer.postalCode || this.customer.postalCode.length < 1) {
			this.snackbarCtrl.open(this.translate.instant('errors.postal'), null, {
				duration: 2000,
			});
			return false;
		}
		if (!this.customer.city || this.customer.city.length < 1) {
			this.snackbarCtrl.open(this.translate.instant('errors.city'), null, {
				duration: 2000,
			});
			return false;
		}
		if (AccountPage.countryList.indexOf(this.customer.country) < 0) {
			this.snackbarCtrl.open(this.translate.instant('errors.country'), null, {
				duration: 2000,
			});
			return false;
		}
		if (
			this.customer.subcard &&
			validator.isInt(this.customer.subcard) &&
			this.customer.subcard.length !== 0 &&
			this.customer.subcard.length !== 16
		) {
			this.snackbarCtrl.open(this.translate.instant('errors.subcard'), null, {
				duration: 2000,
			});
			return false;
		}
		return true;
	}

	async deleteAccount() {
		const alert = await this.alertCtrl.create({
			header: this.translate.instant('account.delete_account.header'),
			message: this.translate.instant('account.delete_account.msg'),
			buttons: [
				{
					text: this.translate.instant('account.delete_account.no'),
				},
				{
					text: this.translate.instant('account.delete_account.yes'),
					handler: async () => {
						this.loading = true;
						try {
							await Api.deleteAccount(environment.customerGroup + '_' + this.user.uid);
							this.snackbarCtrl.open(this.translate.instant('account.delete_account.success'), null, {
								duration: 2000,
							});
							try {
								await this.auth.auth.signOut();
							} catch (logoutError) {
								console.error(logoutError);
							}
							await HomePage.navigate(this.router);
						} catch (e) {
							this.loading = false;
							this.snackbarCtrl.open(this.translate.instant('account.delete_account.error'), null, {
								duration: 2000,
							});
						}
					},
				},
			],
		});
		await alert.present();
		await alert.onDidDismiss();
	}

	goBack() {
		if (this.repository._order === null) {
			HomePage.navigate(this.router);
		} else {
			MenuPage.navigate(this.router);
		}
	}
}
